/**
 * AI assistant spend, per tenant (#861) — a section on Platform Settings.
 *
 * platform_admin only: this is cross-tenant data, and the page it lives on is
 * open to customer_admin. The role check here mirrors the server's 403 rather
 * than standing in for it.
 *
 * Every dollar figure is an ESTIMATE — recorded token usage priced at published
 * per-model rates — and the panel says so, because the number that settles an
 * invoice is the one in the Anthropic console.
 */
import { DollarSign, AlertTriangle, Loader2, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import {
  useAssistantUsage,
  ASSISTANT_USAGE_WINDOW_DAYS,
  type TenantUsage,
} from '../../hooks/useAssistantUsage';

function usd(value: number): string {
  return `$${value.toFixed(2)}`;
}

function tokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

/** Today's spend as a share of the ceiling, clamped for display. */
function ceilingPct(tenant: TenantUsage): number {
  if (tenant.ceiling_usd <= 0) return 0;
  return Math.min(100, (tenant.today_usd / tenant.ceiling_usd) * 100);
}

function TenantRow({ tenant }: { tenant: TenantUsage }) {
  const pct = ceilingPct(tenant);

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ems-text-primary truncate">{tenant.name}</span>
            {!tenant.assistant_enabled && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-ems-bg-card border border-ems-border text-ems-text-muted">
                assistant off
              </span>
            )}
            {tenant.over_ceiling && (
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-ems-red/10 text-ems-red">
                <AlertTriangle className="w-3 h-3" />
                over ceiling
              </span>
            )}
            {tenant.priced_with_fallback && (
              <span
                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-ems-border text-ems-text-muted"
                title="Some turns had no recorded model and were priced at the fallback rate"
              >
                fallback priced
              </span>
            )}
          </div>
          <p className="text-xs text-ems-text-muted mt-0.5">
            {tenant.models.length > 0 ? tenant.models.join(', ') : 'no usage in window'}
            {tenant.total_tokens > 0 && <> · {tokens(tenant.total_tokens)} tokens</>}
          </p>
        </div>

        <div className="flex items-center gap-6 shrink-0 text-right">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-ems-text-muted">Today</p>
            <p
              className={`text-sm font-semibold tabular-nums ${
                tenant.over_ceiling ? 'text-ems-red' : 'text-ems-text-primary'
              }`}
            >
              {usd(tenant.today_usd)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-ems-text-muted">
              {ASSISTANT_USAGE_WINDOW_DAYS}-day
            </p>
            <p className="text-sm font-semibold tabular-nums text-ems-text-primary">
              {usd(tenant.total_usd)}
            </p>
          </div>
        </div>
      </div>

      {/* Today against the ceiling. The ceiling is a warning threshold, not a cap. */}
      <div className="mt-2 flex items-center gap-2">
        <div className="h-1 flex-1 rounded-full bg-ems-border overflow-hidden">
          <div
            className={`h-full rounded-full ${tenant.over_ceiling ? 'bg-ems-red' : 'bg-ems-green'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-[10px] text-ems-text-muted tabular-nums">
          ceiling {usd(tenant.ceiling_usd)}
          {tenant.ceiling_is_default && ' (default)'}
        </span>
      </div>
    </div>
  );
}

export function AssistantSpendSettings() {
  const role = useAuthStore((s) => s.user?.role);
  const { data, isLoading, isError } = useAssistantUsage();

  // Cross-tenant data: not rendered, and not requested, for anyone else.
  if (role !== 'platform_admin') return null;

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
      <div className="px-6 py-4 border-b border-ems-border">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-ems-green" />
          <h2 className="text-sm font-semibold text-ems-text-primary">AI Assistant Spend</h2>
        </div>
        <p className="text-xs text-ems-text-muted mt-0.5">
          Estimated token cost per tenant over the last {ASSISTANT_USAGE_WINDOW_DAYS} days. A tenant
          over its daily ceiling raises a security alert — chat is not blocked.
        </p>
      </div>

      {isLoading && (
        <div className="px-6 py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-ems-green" />
        </div>
      )}

      {isError && (
        <div className="px-6 py-6 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-ems-red shrink-0" />
          <p className="text-sm text-ems-text-muted">
            Could not read assistant usage. No figures are shown rather than zeros, which would read
            as "no spend".
          </p>
        </div>
      )}

      {data && (
        <>
          {data.schema_pending && (
            <div className="px-6 py-3 border-b border-ems-border flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-ems-amber shrink-0 mt-0.5" />
              <p className="text-xs text-ems-text-muted">
                Migration 087 is not applied on this database, so no usage could be read. The figures
                below are empty, not zero.
              </p>
            </div>
          )}
          <div className="divide-y divide-ems-border">
            {data.tenants.map((tenant) => (
              <TenantRow key={tenant.customer_id} tenant={tenant} />
            ))}
          </div>
          <div className="px-6 py-3 border-t border-ems-border">
            <p className="text-[11px] text-ems-text-muted">
              Estimates from recorded token usage at published per-model rates. The authoritative
              figure is the Anthropic console; the hard limit is the workspace monthly spend limit on
              each tenant's key. Default ceiling {usd(data.default_ceiling_usd)}/tenant/day.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
